import { Stack } from '@mui/joy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { UpsertLocation, useAddContactGroupByEmailContext } from 'contacts/components';
import { Location } from 'contacts/elements';
import { useGetContactGroupAddresses } from 'contacts/hooks';
import { IContactGroupAddress } from 'contacts/types';
import { Button, ExpandableCard } from 'core/components';
import { AddButton, Loader } from 'core/elements';
import { AlternateVersionHelper } from 'core/helpers';
import { Color, Variant } from 'theme/enums';

interface IProps {
  level?: number;
}

export const Locations = ({ level = 0 }: IProps) => {
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
  const [editLocation, setEditLocation] = useState<IContactGroupAddress | null>(null);

  const { t } = useTranslation();

  const { user } = useAuthUser();

  const { contactGroup, contactGroupType } = useAddContactGroupByEmailContext();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const { records, isLoading } = useGetContactGroupAddresses(
    {
      filters: {
        contactGroupId: contactGroup?.id,
      },
    },
    {
      enabled: !!contactGroup?.id,
    }
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Stack gap={2}>
        <AddButton title={t('buttonLabelAddLocation')} onClick={() => setIsAddLocationOpen(true)} />

        {records?.map((record) => {
          const title = [
            record.name,
            isAlternateVersionEnabled &&
              record.accountsReceivableId &&
              `[${record.accountsReceivableId}]`,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <ExpandableCard key={record.id} title={title} defaultExpanded={true}>
              <>
                <Stack gap={0}>
                  <Location contactGroupAddress={record} contactGroupType={contactGroupType} />
                </Stack>

                <Button
                  text={t('buttonLabelEditLocation')}
                  variant={Variant.PLAIN}
                  color={Color.PRIMARY}
                  onClick={() => setEditLocation(record)}
                  sx={{
                    width: 'fit-content',
                    paddingRight: 0,
                    paddingLeft: 0,
                    ':hover': { backgroundColor: 'transparent', fontWeight: 800 },
                  }}
                />
              </>
            </ExpandableCard>
          );
        })}
      </Stack>

      <UpsertLocation
        isOpen={isAddLocationOpen || !!editLocation?.id}
        onClose={() => {
          setIsAddLocationOpen(false);
          setEditLocation(null);
        }}
        location={editLocation || undefined}
        contactGroup={contactGroup}
        contactGroupType={contactGroupType}
        onSubmitSuccess={() => setEditLocation(null)}
        level={level + 1}
      />
    </>
  );
};
